import { arrayOfAll } from '#src/app/shared/utils/arrays';

import type { SearchState, SortByEnum, StatusFilter } from './types';

export const SEARCH_URL_KEYS = [
	'q',
	'page',
	'sort',
	'status',
	'topics',
	'city',
	'country',
	// Legacy keys from pre-migration URLs — kept so useLandingPage doesn't treat them as empty search
	'victory',
	'weekly_signature_count',
	'location_city',
	'location_country_code',
] as const;

function parsePage(raw: string | null): number {
	// Fall back to page 0 for missing or non-numeric values (e.g. ?page=foo) so NaN never
	// reaches the search request params or the pagination UI.
	const parsed = parseInt(raw ?? '', 10);
	return Number.isNaN(parsed) ? 0 : Math.max(0, parsed - 1);
}

const VALID_STATUSES = arrayOfAll<StatusFilter>()(['all', 'victory', 'trending']);

function parseStatus(raw: string | null, params: URLSearchParams): StatusFilter {
	// Prefer the new `status` key; map legacy boolean flags as fallback.
	if (raw && (VALID_STATUSES as readonly string[]).includes(raw)) return raw as StatusFilter;
	if (params.get('victory') === 'true') return 'victory';
	if (params.get('weekly_signature_count') !== null) return 'trending';
	return 'all';
}

function parseTopics(raw: string | null): string[] {
	if (!raw) return [];
	// Legacy topics used '+' as a separator; URLSearchParams decodes '+' as space.
	const separator = raw.includes('|') ? '|' : ' ';
	return raw.split(separator).filter(Boolean).map(decodeURIComponent);
}

function parseMultiValue(raw: string | null, legacyRaw: string | null): string[] {
	if (raw) return raw.split('|').map(decodeURIComponent);
	if (legacyRaw) return [legacyRaw];
	return [];
}

export function parseUrlToState(search: string): SearchState {
	const params = new URLSearchParams(search);

	return {
		query: params.get('q') ?? '',
		page: parsePage(params.get('page')),
		sortBy: (params.get('sort') as SortByEnum) || undefined,
		status: parseStatus(params.get('status'), params),
		topics: parseTopics(params.get('topics')),
		locationCities: parseMultiValue(params.get('city'), params.get('location_city')),
		locationCountries: parseMultiValue(params.get('country'), params.get('location_country_code')),
	};
}

export function stateToUrlSearch(state: SearchState): string {
	const params = new URLSearchParams();

	if (state.query) params.set('q', state.query);
	if (state.page > 0) params.set('page', String(state.page + 1));
	if (state.sortBy) params.set('sort', state.sortBy);
	if (state.status !== 'all') params.set('status', state.status);
	if (state.topics.length > 0) params.set('topics', state.topics.map(encodeURIComponent).join('|'));
	if (state.locationCities.length > 0) params.set('city', state.locationCities.map(encodeURIComponent).join('|'));
	if (state.locationCountries.length > 0)
		params.set('country', state.locationCountries.map(encodeURIComponent).join('|'));

	const str = params.toString();
	return str ? `?${str}` : '';
}

export function hasSearchParams(state: SearchState): boolean {
	return !!(
		state.query ||
		state.page > 0 ||
		state.sortBy ||
		state.status !== 'all' ||
		state.topics.length > 0 ||
		state.locationCities.length > 0 ||
		state.locationCountries.length > 0
	);
}

export function hasFiltersApplied(state: SearchState): boolean {
	return !!(
		state.topics.length > 0 ||
		state.status !== 'all' ||
		state.locationCities.length > 0 ||
		state.locationCountries.length > 0
	);
}
